// <s-scroll-progress> mirrors the horizontal scroll position of its [data-scroll-track] in a
// [data-scroll-thumb] bar (width = visible share, offset = scroll share) and wires optional
// [data-scroll-prev]/[data-scroll-next] buttons that page the track and disable at either end.
// The track itself stays natively scrollable by touch, trackpad and keyboard.
class ScrollProgress extends HTMLElement {
  #track = null;
  #thumb = null;
  #prev = null;
  #next = null;
  #controller = null;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-scroll-track]');
    this.#thumb = this.querySelector('[data-scroll-thumb]');
    if (!this.#track) return;
    this.#prev = this.querySelector('[data-scroll-prev]');
    this.#next = this.querySelector('[data-scroll-next]');
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };
    this.#track.addEventListener('scroll', schedule, { signal, passive: true });
    window.addEventListener('resize', schedule, { signal, passive: true });
    this.#prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.#next?.addEventListener('click', () => this.#page(1), { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #isRtl() {
    return getComputedStyle(this.#track).direction === 'rtl';
  }

  #page(step) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = this.#track.clientWidth * 0.8 * step * (this.#isRtl() ? -1 : 1);
    this.#track.scrollBy({ left: distance, behavior: reduce ? 'auto' : 'smooth' });
  }

  #update() {
    const { scrollWidth, clientWidth } = this.#track;
    const scrollable = scrollWidth - clientWidth;
    const offset = Math.abs(this.#track.scrollLeft);
    if (this.#thumb) {
      const share = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
      const progress = scrollable > 0 ? offset / scrollable : 0;
      this.#thumb.style.width = `${share * 100}%`;
      this.#thumb.style.transform = `translateX(${(this.#isRtl() ? -1 : 1) * progress * ((1 - share) / share) * 100}%)`;
    }
    if (this.#prev) this.#prev.disabled = offset <= 1;
    if (this.#next) this.#next.disabled = offset >= scrollable - 1;
  }
}

if (!customElements.get('s-scroll-progress')) customElements.define('s-scroll-progress', ScrollProgress);
